import React, { useState } from 'react';
import { SECTORS } from '../../../utils/sectors';
import SectorBadge from '../../../components/common/SectorBadge';

const FREQUENCIES = [
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const SUGGESTED = {
  financial:  ['Review budget', 'Check accounts', 'Pay bills', 'No-spend day'],
  career:     ['Deep work block', 'Client outreach', 'Learning/course', 'Team check-in'],
  emotional:  ['Journal', 'Call a friend', 'Therapy session', 'Gratitude practice'],
  mental:     ['Meditation', 'Read', 'Brain dump', 'Limit social media'],
  physical:   ['Workout', 'Walk outside', 'Sleep 7+ hours', 'Meal prep'],
  spiritual:  ['Prayer / quiet time', 'Nature time', 'Reflection', 'Acts of service'],
};

function ActivityForm({ onAdd, activeSector }) {
  const [form, setForm] = useState({
    sector: activeSector || 'physical',
    activity_name: '',
    frequency: 'weekly',
    target_count: 3,
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.activity_name.trim()) return;
    onAdd({ ...form, target_count: Number(form.target_count) });
    setForm(f => ({ ...f, activity_name: '', target_count: 3 }));
  };

  const sector = SECTORS.find(s => s.key === form.sector);

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
      <h4 className="text-sm font-semibold text-reset-brown">Add an activity</h4>

      {/* Sector picker */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Sector</label>
        <div className="flex flex-wrap gap-1.5">
          {SECTORS.map(s => (
            <button
              key={s.key}
              type="button"
              onClick={() => setForm(f => ({ ...f, sector: s.key }))}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition
                ${form.sector === s.key
                  ? `${s.badgeClass} ring-2 ring-offset-1`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
            >
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Suggestions */}
      {SUGGESTED[form.sector] && (
        <div>
          <p className="text-xs text-gray-400 mb-1">Quick suggestions:</p>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTED[form.sector].map(suggestion => (
              <button
                key={suggestion}
                type="button"
                onClick={() => setForm(f => ({ ...f, activity_name: suggestion }))}
                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full transition"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Activity name */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Activity name</label>
        <input
          type="text"
          value={form.activity_name}
          onChange={e => setForm(f => ({ ...f, activity_name: e.target.value }))}
          placeholder="e.g. Morning walk, Journaling…"
          maxLength={255}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          required
        />
      </div>

      {/* Frequency + count */}
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-xs text-gray-500 mb-1">Frequency</label>
          <select
            value={form.frequency}
            onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            {FREQUENCIES.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
        <div className="w-24">
          <label className="block text-xs text-gray-500 mb-1">Times</label>
          <input
            type="number"
            min={1}
            max={99}
            value={form.target_count}
            onChange={e => setForm(f => ({ ...f, target_count: e.target.value }))}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      </div>

      <button
        type="submit"
        className="w-full bg-reset-terracotta text-white py-2 rounded-xl text-sm font-medium hover:opacity-90 transition"
      >
        + Add activity
      </button>
    </form>
  );
}

export default function ActivitiesStep({ onNext, onBack, initialActivities }) {
  const [activities, setActivities] = useState(initialActivities || []);

  const addActivity = (activity) => {
    setActivities(prev => [...prev, { ...activity, _tempId: Date.now() }]);
  };

  const removeActivity = (tempId) => {
    setActivities(prev => prev.filter(a => a._tempId !== tempId));
  };

  // Group activities by sector for display
  const bySector = SECTORS.map(s => ({
    sector: s,
    items: activities.filter(a => a.sector === s.key),
  })).filter(g => g.items.length > 0);

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <h2 className="text-xl font-bold text-reset-brown mb-1">Your planned activities</h2>
      <p className="text-sm text-gray-500 mb-6 leading-relaxed">
        What are you committing to doing? Add the activities you want the system to track.
        These are what it watches — if you&apos;re not doing them, it notices.
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Aim for 2–4 activities across your priority sectors. You can always add more later.
      </p>

      {/* Added activities */}
      {bySector.length > 0 && (
        <div className="mb-6 space-y-3">
          {bySector.map(({ sector, items }) => (
            <div key={sector.key} className={`rounded-2xl p-3 ${sector.bgClass}`}>
              <div className="flex items-center gap-1.5 mb-2">
                <span>{sector.emoji}</span>
                <span className={`text-xs font-semibold ${sector.textClass}`}>{sector.label}</span>
              </div>
              <div className="space-y-1.5">
                {items.map(activity => (
                  <div key={activity._tempId} className="flex items-center justify-between bg-white rounded-xl px-3 py-2">
                    <div>
                      <span className="text-sm font-medium text-gray-800">{activity.activity_name}</span>
                      <span className="text-xs text-gray-400 ml-2">
                        {activity.target_count}× {activity.frequency}
                      </span>
                    </div>
                    <button
                      onClick={() => removeActivity(activity._tempId)}
                      className="text-gray-300 hover:text-red-400 text-lg leading-none transition"
                      aria-label="Remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {activities.length === 0 && (
        <div className="text-center py-6 text-gray-400 text-sm mb-4">
          No activities yet — add your first one below.
        </div>
      )}

      {/* Add activity form */}
      <ActivityForm onAdd={addActivity} />

      <div className="flex gap-3 mt-6">
        <button
          onClick={onBack}
          className="flex-1 border border-gray-300 text-gray-600 py-3 rounded-xl text-sm font-medium hover:bg-gray-50 transition"
        >
          ← Back
        </button>
        <button
          onClick={() => onNext(activities)}
          disabled={activities.length === 0}
          className="flex-[2] bg-reset-terracotta text-white py-3 rounded-xl font-semibold text-sm hover:opacity-90 disabled:opacity-40 transition"
        >
          Next: How to reach me →
        </button>
      </div>

      {activities.length === 0 && (
        <p className="text-center text-xs text-gray-400 mt-2">Add at least one activity to continue</p>
      )}
    </div>
  );
}
